import React, { useEffect, useRef, useState } from "react";
import { School } from "../../interfaces/school";
import { useLocation, useNavigate } from "react-router-dom";
import { getSchoolsWithParams } from "../../services/schoolService";
import ToolTip from "../../components/component-tools/ToolTip";

const Schools = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allSchools, setAllSchools] = useState<School[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const observer = useRef<IntersectionObserver | null>(null);
  const lastSchoolRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmedSearchTerm = searchTerm.trim();
      setDebouncedSearchTerm(trimmedSearchTerm);

      if (trimmedSearchTerm === "") {
        setPageNumber(1);
        setHasMore(true);
      } else {
        setAllSchools([]);
        setPageNumber(1);
        setHasMore(true);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoading(true);

        const { schools, totalRecords } = await getSchoolsWithParams(
          pageNumber,
          100,
          debouncedSearchTerm
        );

        setAllSchools((prevSchools) => {
          const existingIds = new Set(
            prevSchools.map((school) => school.schoolID)
          );
          const uniqueSchools = schools.filter(
            (o) => !existingIds.has(o.schoolID)
          );
          const combined = [...prevSchools, ...uniqueSchools];

          if (combined.length >= totalRecords) setHasMore(false);
          return combined;
        });
      } catch (error) {
        setErrorMessage("Failed to load schools.");
        console.error("Error fetching schools:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, [debouncedSearchTerm, pageNumber]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, location.pathname, navigate]);

  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (lastSchoolRef.current) observer.current.observe(lastSchoolRef.current);

    return () => observer.current?.disconnect();
  }, [hasMore, loading]);

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          All Schools
          <ToolTip
            tooltipText="Master list of all schools"
            placement="right"
            className="info-icon"
          />
        </h1>
        <div className="page-items-container">
          <div className="search-container" style={{ width: "375px" }}>
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search By School Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              ></i>
            ) : (
              <i className="bi bi-search search-icon"></i>
            )}
          </div>
          <button
            className="oxbtn btn-new"
            onClick={() => navigate(`/admin/schools/create-school`)}
          >
            <div> New School </div>
            <i className="bi bi-plus-circle-fill plus-icon"></i>
          </button>
        </div>
      </div>

      {loading && pageNumber === 1 ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="table-wrapper">
            <div className="table-container" style={{ fontSize: "large" }}>
              <table className="table" style={{ cursor: "pointer" }}>
                <thead>
                  <tr>
                    <th style={{ width: "250px" }}>School</th>
                    <th style={{ width: "150px" }}>State</th>
                    <th style={{ width: "200px" }}>City</th>
                    <th style={{ width: "250px" }}>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {allSchools.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center" }}>
                        No schools found.
                      </td>
                    </tr>
                  ) : (
                    allSchools.map((school) => (
                      <tr
                        key={school.schoolID}
                        onClick={() =>
                          navigate(
                            `/admin/schools/view-school/${school.schoolID}`
                          )
                        }
                      >
                        <td>
                          <u>{school.name}</u>
                        </td>
                        <td>{school.state}</td>
                        <td>{school.city}</td>
                        <td>{school.contact}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div ref={lastSchoolRef} style={{ height: "1px" }}></div>
            </div>
          </div>
        </>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default Schools;
